import { create } from "zustand";
import { getUserDetails } from "@/services/app/index";
import { getNotificationsSnapshot } from "@/services/notifications";
import { getShoppingCart } from "@/services/cart";
import { OPRSessionManagement } from "../utils/sessionManagement";
import { http } from "@/services/http";

const opinFormatter = (string = "") => {
  const SEPARATOR = "-";

  if (string.length !== 8 || string.includes(SEPARATOR)) {
    return string;
  }

  return `${string.slice(0, 4)}${SEPARATOR}${string.slice(4)}`;
};

export const useAppStore = create((set) => ({
  unreadCount: 0,
  user: null,
  isLoading: true,
  loadingUser: false,
  loadingNotifications: false,
  loadingShoppingCart: false,
  shoppingCart: null,
  checkSessionInitialized: false,
  countDown: "5:00",
  countDownId: null,
  showSessionTimeoutAlert: false,
  isRedirecting: false,
  isForceUpdate: false,
  isCaptchaRequired: false,
  captchaKey: "",
  systemNotifications: [],
  setUser: async () => {
    const { loadingUser } = useAppStore.getState();
    if (loadingUser) return;
    set((state) => ({ ...state, loadingUser: true }));
    const userDetails = await getUserDetails();

    if (userDetails) {
      userDetails.opin_formatted = opinFormatter(userDetails.opin);
    }

    set({
      user: userDetails,
      loadingUser: false,
      isLoading: false,
      isCaptchaRequired: !!userDetails?.captcha_required,
      captchaKey: userDetails?.captcha_key,
      systemNotifications: userDetails?.system_notifications || [],
    });
  },
  setIsCaptchaRequired: (isCaptchaRequired) =>
    set((state) => ({ ...state, isCaptchaRequired })),
  adjustUnreadCount: (number) => {
    const { unreadCount } = useAppStore.getState();
    if (unreadCount === 0) return;
    set((state) => ({ ...state, unreadCount: Number(unreadCount) + number }));
  },
  fetchNotificationSnapshot: async () => {
    const { loadingNotifications } = useAppStore.getState();
    if (loadingNotifications) return;
    set((state) => ({ ...state, loadingNotifications: true }));
    try {
      const { unread } = await getNotificationsSnapshot();
      set((state) => ({
        ...state,
        error: "",
        unreadCount: unread,
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error: error.message,
      }));
    } finally {
      set((state) => ({
        ...state,
        loadingNotifications: false,
      }));
    }
  },
  fetchShoppingCart: async () => {
    const { loadingShoppingCart } = useAppStore.getState();
    if (loadingShoppingCart) return;
    set((state) => ({ ...state, loadingShoppingCart: true }));
    try {
      const shoppingCart = await getShoppingCart();
      set((state) => ({
        ...state,
        error: "",
        shoppingCart,
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error: error.message,
      }));
    }
    set((state) => ({
      ...state,
      loadingShoppingCart: false,
    }));
  },
  initializeCheckSession: () => {
    const { checkSessionInitialized, checkSession } = useAppStore.getState();
    if (!checkSessionInitialized) {
      checkSession();
      set((state) => ({ ...state, checkSessionInitialized: true }));
    }
  },
  checkSession: async () => {
    const {
      checkSession,
      setCountDown,
      setCountDownId,
      setShowSessionTimeoutAlert,
    } = useAppStore.getState();
    const fiveMinuteExpiryDate = new Date();
    fiveMinuteExpiryDate.setMinutes(fiveMinuteExpiryDate.getMinutes() + 5);
    const fiveMinutesFromCurrentDate = new Date(fiveMinuteExpiryDate);

    const logInEventBeenSet = OPRSessionManagement.hasLogInEventBeenSet();
    const isExpired = OPRSessionManagement.isSession45Expired();
    if (logInEventBeenSet) {
      if (isExpired) {
        try {
          if (!window.location.hash.includes("forcedUpdate")) {
            const sessionTimeLeftResponse = await http.get(
              "/api/session/sessionTimeLeft"
            );
            if (sessionTimeLeftResponse.data.session_time_left > 300) {
              setTimeout(checkSession, 5000);
              return;
            }
          }
        } catch (e) {
          OPRSessionManagement.destroySession();
          window.location =
            "/user/logout?message=isExpiredSessionTimeLeftFailed";
        }

        setShowSessionTimeoutAlert(true);

        function updateTimer() {
          const id = setTimeout(function () {
            const currentDate = new Date();
            const timeDifference =
              fiveMinutesFromCurrentDate.getTime() - currentDate.getTime();
            const timeDifferenceInSeconds = Math.floor(timeDifference / 1000);
            const mins = (
              "0" + Math.floor(timeDifferenceInSeconds / 60)
            ).substr(-2);
            const secs = ("0" + (timeDifferenceInSeconds % 60)).substr(-2);

            setCountDown(`${mins}:${secs}`);

            if (timeDifferenceInSeconds <= 0) {
              OPRSessionManagement.destroySession();
              window.location = "/user/logout?message=timeoutRanOut";
            } else {
              updateTimer();
            }
          }, 500);
          setCountDownId(id);
        }
        updateTimer();
      }
    } else {
      if (
        location.pathname !== "/" &&
        location.pathname !== "/user/login" &&
        location.pathname !== "/cart/training_list" &&
        !location.pathname.startsWith("/cart/view_training")
      ) {
        window.location =
          "/user/logout?message=loginEventNotSetAndPathDoesNotMatchSlash";
      }
    }

    if (!isExpired) {
      setTimeout(checkSession, 5000); // check every 5 seconds
    }
  },
  setCountDownId: (id) => set((state) => ({ ...state, countDownId: id })),
  setCountDown: (countDown) => set((state) => ({ ...state, countDown })),
  setShowSessionTimeoutAlert: (showSessionTimeoutAlert) =>
    set((state) => ({ ...state, showSessionTimeoutAlert })),
  setIsRedirecting: (isRedirecting) =>
    set((state) => ({ ...state, isRedirecting })),
  setIsForceUpdate: (isForceUpdate) =>
    set((state) => ({ ...state, isForceUpdate })),
  checkFeatureFlag: (featureFlag) => {
    const { user } = useAppStore.getState();
    if (!user) return false;
    return user.features.includes(featureFlag);
  },
}));
